import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEventIdFromCustomUrl, getEvent, getProfileIdFromUsername, getPlayerProfile, debugGetAllCustomUrls } from '../services/firestoreService';
import EventPreview from '../pages/EventPreview';
import PlayerProfilePreview from '../pages/PlayerProfilePreview';
import NotFound from '../pages/NotFound';

const CustomUrlRedirect = () => {
  const { eventId: urlExtension } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [eventId, setEventId] = useState(null);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCustomUrlRedirect = async () => {
      try {
        setIsLoading(true);
        
        // First, try to get the event ID from the custom URL
        const resolvedEventId = await getEventIdFromCustomUrl(urlExtension);
        
        if (resolvedEventId) {
          // Custom event URL found
          setEventId(resolvedEventId);
        } else {
          // No custom event URL found, check for player profile
          const playerInfo = await getProfileIdFromUsername(urlExtension);
          
          if (playerInfo) {
            // Check if profileId is valid
            if (!playerInfo.profileId) {
              setError('Profile not found or incomplete');
              return;
            }
            
            // Check if it's a premium custom URL (without /players/ prefix)
            if (playerInfo.isPremium) {
              // Load the player profile
              const profile = await getPlayerProfile(playerInfo.profileId);
              if (profile && profile.status === 'published') {
                setPlayerProfile({ ...profile, username: urlExtension });
              } else {
                setError('Profile not found or not published');
              }
            } else {
              // Non-premium users should use /players/username format
              navigate(`/players/${urlExtension}`, { replace: true });
              return;
            }
          } else {
            // No player profile found, check if it's a direct event ID
            // This maintains backward compatibility with existing event URLs
            const event = await getEvent(urlExtension);
            
            if (event) {
              setEventId(urlExtension);
            } else {
              // Neither custom URL, player profile, nor direct event ID found
              setError('Page not found');
            }
          }
        }
      } catch (error) {
        console.error('CustomUrlRedirect: Error occurred:', error);
        setError('Failed to load page');
      } finally {
        setIsLoading(false);
      }
    };

    if (urlExtension) {
      handleCustomUrlRedirect();
    } else {
      setIsLoading(false);
      setError('No URL provided');
    }
  }, [urlExtension]);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <NotFound />;
  }

  // Render the appropriate component based on what was found
  if (playerProfile) {
    // Pass the profile data directly to avoid routing issues
    return <PlayerProfilePreview profileData={playerProfile} username={urlExtension} />;
  } else if (eventId) {
    return <EventPreview eventId={eventId} />;
  }

  return null;
};

export default CustomUrlRedirect; 